import { useForm } from "react-hook-form";
import { useState } from "react";
import { UpdatePasswordRequest } from "../../types/user.type";
import { toast } from "react-toastify";
import { useUpdatePasswordMutation } from "../../redux/api/profile.api";
import { FaEye, FaEyeSlash } from "react-icons/fa";  // Import FontAwesome icons
import { useNavigate } from "react-router-dom";

const getPasswordStrength = (password: string) => {
  const lengthCriteria = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  let strength = 0;
  if (lengthCriteria) strength += 1;
  if (hasUpperCase) strength += 1;
  if (hasLowerCase) strength += 1;
  if (hasNumbers) strength += 1;
  if (hasSpecialChars) strength += 1;

  if (strength <= 2) return { className: "bg-red-500", width: "30%" };
  if (strength === 3) return { className: "bg-yellow-500", width: "60%" };

  return { className: "bg-green-500", width: "100%" };
};

interface UpdatePasswordProps {
  setActiveTab: (tab: string) => void;
}
export default function UpdatePassword({ setActiveTab }: UpdatePasswordProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<UpdatePasswordRequest>({
    defaultValues: {
      OldPassWord: "",
      NewPassWord: "",
      ConfirmPassWord: "",
    },
  });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [updatePassword] = useUpdatePasswordMutation();

  const [showOldPassword, setShowOldPassword] = useState(false);  // state for toggling old password visibility
  const [showNewPassword, setShowNewPassword] = useState(false);  // state for toggling new password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);  // state for toggling confirm password visibility

  const newPassword = watch("NewPassWord");

  const strengthBarStyles = getPasswordStrength(newPassword || "");

  const onSubmit = async (data: UpdatePasswordRequest) => {
    setLoading(true);
    try {
      await updatePassword(data)
        .unwrap()
        .then((res) => {
          toast.success("Password updated successfully!");
          setActiveTab("about");
        });
    } catch (error: any) {
      if (error.status === 'NETWORK_ERROR' || error.status === 'BAD_REQUEST')
        return;
      toast.error("Failed to update password. Please try againn.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full justify-center dark:text-slate-50 items-center   ">
      <div className="w-full shadow-sm   p-6 rounded-lg ">
        <h2 className="text-2xl font-semibold text-center mb-4">Update Password</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-100">
              Old Password
            </label>
            <div className="relative">
              <input
                type={showOldPassword ? "text" : "password"}
                {...register("OldPassWord", { required: "Old password is required" })}
                className="w-full p-2 text-xs rounded border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowOldPassword(!showOldPassword)}
                className="absolute top-1/2 right-2 transform -translate-y-1/2"
              >
                {showOldPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.OldPassWord && (
              <p className="text-red-500 text-xs">{errors.OldPassWord.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-100">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                {...register("NewPassWord", { required: "New password is required" })}
                className="w-full p-2 text-xs rounded border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute top-1/2 right-2 transform -translate-y-1/2"
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.NewPassWord && (
              <p className="text-red-500 text-xs">{errors.NewPassWord.message}</p>
            )}
          </div>

          {/* Password Strength Bar */}
          {newPassword && <div className="mt-2">
            <div className="w-full h-2 dark:bg-gray-700 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${strengthBarStyles.className} transition-all duration-300`}
                style={{ width: strengthBarStyles.width }}
              ></div>
            </div>
          </div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-100">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                {...register("ConfirmPassWord", { required: "Please confirm your password" })}
                className="w-full p-2 text-xs rounded border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute top-1/2 right-2 transform -translate-y-1/2"
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.ConfirmPassWord && (
              <p className="text-red-500 text-xs">{errors.ConfirmPassWord.message}</p>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-accent-500/95 text-white py-2 px-4 rounded-md shadow-sm hover:bg-accent-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-85"
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
